import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  Search,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  Rocket,
  Gamepad2,
  Terminal,
  Book,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Paperclip,
  Download,
  Tag,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { wikiService, WikiCategory, WikiPage as WikiPageType } from '@/lib/services/wiki.service';
import PageHero from '@/components/common/PageHero';
import { toast } from 'sonner';
import { sanitizeHtml } from '@/lib/sanitize';
import { useTranslation } from 'react-i18next';
import { Caixa } from '@/construtor/caixa';
import { EspacoDeAnuncio } from '@/templates/comum/anuncios/EspacoDeAnuncio';

const iconMap: Record<string, any> = {
  Rocket,
  Gamepad2,
  Terminal,
  Book,
};

export default function WikiPage() {
  const { t } = useTranslation();
  const { categoryId, articleId } = useParams();
  const { config } = useTheme();
  const { store } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);
  const [categories, setCategories] = useState<WikiCategory[]>([]);
  const [pages, setPages] = useState<WikiPageType[]>([]);
  const [currentPage, setCurrentPage] = useState<WikiPageType | null>(null);
  const [loading, setLoading] = useState(true);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [wikiEnabled, setWikiEnabled] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [popularTags, setPopularTags] = useState<any[]>([]);
  const [allPages, setAllPages] = useState<WikiPageType[]>([]);
  const [mostViewedPages, setMostViewedPages] = useState<WikiPageType[]>([]);

  useEffect(() => {
    if (store?.plan?.limits) {
      const maxWikiPages = store.plan.limits.maxWikiPages ?? 0;
      setWikiEnabled(maxWikiPages !== 0);
    }
  }, [store]);

  useEffect(() => {
    if (store?.id && wikiEnabled) {
      loadWikiData();
    } else {
      setLoading(false);
    }
  }, [store?.id, wikiEnabled, categoryId]);

  useEffect(() => {
    if (store?.id && articleId && wikiEnabled) {
      loadPageBySlug(articleId);
    } else {
      setCurrentPage(null);
    }
  }, [store?.id, articleId, wikiEnabled]);

  useEffect(() => {
    if (store?.id && wikiEnabled) {
      loadWikiData();
    }
  }, [selectedTags]);

  const loadWikiData = async () => {
    if (!store?.id) return;

    try {
      setLoading(true);
      console.log('[WikiPage] Loading wiki data for store:', store.id);
      
      // Load categories and popular tags
      const [categoriesData, popularTagsData, mostViewedData] = await Promise.all([
        wikiService.getCategories(store.id),
        wikiService.getPopularTags(store.id),
        wikiService.getMostViewedPages(store.id, 5),
      ]);
      
      setCategories(categoriesData);
      setPopularTags(popularTagsData);
      setMostViewedPages(mostViewedData);
      
      // Find category by slug if categoryId param is provided
      const categoryIdToFetch = categoryId 
        ? categoriesData.find(c => c.slug === categoryId)?.id 
        : undefined;
      
      console.log('[WikiPage] CategoryId param (slug):', categoryId);
      console.log('[WikiPage] Resolved categoryId (ID):', categoryIdToFetch);
      
      // Load pages for the category
      const pagesData = await wikiService.getPages(store.id, categoryIdToFetch, selectedTags);
      
      console.log('[WikiPage] Categories loaded:', categoriesData);
      console.log('[WikiPage] Pages loaded:', pagesData);
      console.log('[WikiPage] Popular tags loaded:', popularTagsData);
      
      setPages(pagesData);
      setAllPages(pagesData);
    } catch (error) {
      console.error('Erro ao carregar wiki:', error);
      toast.error('Erro ao carregar conteúdo da wiki');
    } finally {
      setLoading(false);
    }
  };

  const loadPageBySlug = async (slug: string) => {
    if (!store?.id) return;

    try {
      const page = await wikiService.getPageBySlug(store.id, slug);
      setCurrentPage(page);
    } catch (error) {
      console.error('Erro ao carregar página:', error);
      toast.error('Página não encontrada');
      setCurrentPage(null);
    }
  };

  const handleFeedback = async (isHelpful: boolean) => {
    if (!store?.id || !currentPage || submittingFeedback) return;

    try {
      setSubmittingFeedback(true);
      setFeedback(isHelpful ? 'positive' : 'negative');
      
      await wikiService.submitFeedback(store.id, currentPage.id, {
        isHelpful,
      });
      
      toast.success(
        isHelpful
          ? 'Obrigado pelo feedback positivo!'
          : 'Obrigado pelo feedback! Vamos melhorar este conteúdo.'
      );
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      toast.error('Erro ao enviar feedback');
      setFeedback(null);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const getRelatedPages = () => {
    if (!currentPage || !currentPage.tags || currentPage.tags.length === 0) {
      return [];
    }

    const currentPageTagIds = currentPage.tags.map(t => t.id);
    
    return allPages
      .filter(page => {
        // Don't include current page
        if (page.id === currentPage.id) return false;
        
        // Check if page has any common tags
        if (!page.tags || page.tags.length === 0) return false;
        
        const pageTagIds = page.tags.map(t => t.id);
        return currentPageTagIds.some(tagId => pageTagIds.includes(tagId));
      })
      .sort((a, b) => {
        // Sort by number of common tags (descending)
        const aCommonTags = a.tags?.filter(t => currentPageTagIds.includes(t.id)).length || 0;
        const bCommonTags = b.tags?.filter(t => currentPageTagIds.includes(t.id)).length || 0;
        return bCommonTags - aCommonTags;
      })
      .slice(0, 3); // Show max 3 related articles
  };

  if (!wikiEnabled) {
    console.log('[WikiPage] Wiki not enabled, redirecting to 404');
    console.log('[WikiPage] wikiEnabled:', wikiEnabled);
    console.log('[WikiPage] store:', store);
    return <Navigate to="/404" replace />;
  }

  const currentCategory = categoryId
    ? categories.find((c) => c.slug === categoryId)
    : null;

  console.log('[WikiPage] categoryId from params:', categoryId);
  console.log('[WikiPage] currentCategory:', currentCategory);
  console.log('[WikiPage] all pages:', pages);

  const categoryPages = currentCategory
    ? pages.filter((p) => p.categoryId === currentCategory.id)
    : [];

  console.log('[WikiPage] categoryPages:', categoryPages);

  const filteredPages = searchQuery
    ? pages.filter((page) =>
        page.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const getAdjacentPages = () => {
    if (!currentPage || !currentCategory) return { prev: null, next: null };

    const pageIndex = categoryPages.findIndex((p) => p.id === currentPage.id);
    const prev = pageIndex > 0 ? categoryPages[pageIndex - 1] : null;
    const next = pageIndex < categoryPages.length - 1 ? categoryPages[pageIndex + 1] : null;

    return { prev, next };
  };

  const { prev, next } = getAdjacentPages();

  if (loading) {
    return (
      <div>
        <PageHero
          title="Wiki"
          breadcrumbs={[{ label: 'Wiki' }]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Wiki"
        breadcrumbs={[
          { label: 'Wiki', href: '/wiki' },
          ...(currentCategory
            ? [
                {
                  label: currentCategory.name,
                  href: currentPage ? `/wiki/${currentCategory.slug}` : undefined,
                },
              ]
            : []),
          ...(currentPage ? [{ label: currentPage.title }] : []),
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 -mt-24 pt-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          {categories.length > 0 && (
            <aside className="lg:col-span-1">
            <Caixa id="wiki.lateral" miolo>
            <Card className="bg-card border-border sticky top-24">
              <CardContent className="p-4">
                {/* Search */}
                <Caixa id="wiki.lateral.busca">
                <div className="relative mb-6">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar artigos..."
                    className="pl-10 bg-muted border-border text-foreground"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                </Caixa>

                {/* Search Results */}
                {searchQuery && (
                  <Caixa id="wiki.lateral.resultados">
                  <div className="mb-6">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                      {t('store.wiki.results', { count: filteredPages.length })}
                    </p>
                    {filteredPages.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {t('store.wiki.noResults')}
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {filteredPages.slice(0, 5).map((page) => (
                          <Link
                            key={page.id}
                            to={`/wiki/${page.categoryId}/${page.slug}`}
                            className="block p-2 rounded-lg hover:bg-muted transition-colors"
                            onClick={() => setSearchQuery('')}
                          >
                            <p className="text-sm text-foreground">{page.title}</p>
                          </Link>
                        ))}
                      </div>
                    )}
                    <Separator className="my-4 bg-border" />
                  </div>
                  </Caixa>
                )}
                {/* Popular Tags */}
                {popularTags.length > 0 && (
                  <>
                    <Caixa id="wiki.lateral.tags">
                    <div className="mb-6">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
                        🏷️ Tags Populares
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {popularTags.map((tag) => {
                          const tagColor = tag.color || 'hsl(var(--primary))';
                          const isSelected = selectedTags.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(tag.id)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                isSelected ? 'ring-2 ring-offset-2 ring-offset-background' : 'hover:opacity-80'
                              }`}
                              style={{
                                backgroundColor: isSelected ? tagColor : `${tagColor}15`,
                                color: isSelected ? '#fff' : tagColor,
                                borderColor: `${tagColor}30`,
                                borderWidth: '1px',
                              }}
                            >
                              <Tag className="w-3 h-3" />
                              <span>{tag.name}</span>
                              {tag.count !== undefined && (
                                <span className={isSelected ? 'opacity-90' : 'opacity-60'}>
                                  ({tag.count})
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {selectedTags.length > 0 && (
                        <button
                          onClick={() => setSelectedTags([])}
                          className="mt-3 text-xs text-primary hover:text-primary/80 transition-colors"
                        >
                          Limpar filtros
                        </button>
                      )}
                    </div>
                    </Caixa>
                    <Separator className="my-4 bg-border" />
                  </>
                )}
                {/* Categories */}
                <Caixa id="wiki.lateral.categorias">
                <div className="space-y-2">
                  {categories.map((category) => {
                    const Icon = category.iconName && iconMap[category.iconName] ? iconMap[category.iconName] : Book;
                    const isActive = currentCategory?.slug === category.slug;
                    const catPages = pages.filter(p => p.categoryId === category.id);

                    return (
                      <div key={category.id}>
                        <Link
                          to={`/wiki/${category.slug}`}
                          className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary/10 text-primary'
                              : 'hover:bg-muted text-foreground'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="font-medium">{category.name}</span>
                        </Link>

                        {/* Category pages */}
                        {isActive && (
                          <div className="ml-6 mt-1 space-y-1">
                            {catPages.map((page) => (
                              <Link
                                key={page.id}
                                to={`/wiki/${category.slug}/${page.slug}`}
                                className={`block p-2 rounded-lg text-sm transition-colors ${
                                  currentPage?.id === page.id
                                    ? 'text-primary font-medium'
                                    : 'text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {page.title}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </Caixa>
              </CardContent>
            </Card>
            </Caixa>
          </aside>
          )}

          {/* Main Content */}
          <main className={categories.length > 0 ? "lg:col-span-3" : "lg:col-span-4"}>
            {!currentCategory && !currentPage ? (
              /* Categories Overview */
              <>
                {categories.length === 0 ? (
                  <Caixa id="wiki.vazio" miolo>
                  <Card className="border-card-border">
                    <CardContent className="p-12">
                      <div className="flex flex-col items-center gap-4 text-center">
                        {/*
                          Mesma bolha dos outros vazios da loja: chapada, na
                          cor do lojista. O gradiente anterior puxava metade
                          para a cor de destaque, então o ícone aparecia num
                          tom que a loja não escolheu para ele.
                        */}
                        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Book className="w-10 h-10 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-foreground mb-2">
                            {t('store.wiki.emptyTitle')}
                          </h3>
                          <p className="text-muted-foreground text-lg">
                            {t('store.wiki.emptyBody')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                ) : (
                  <Caixa id="wiki.categorias">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {categories.map((category) => {
                    const Icon = category.iconName && iconMap[category.iconName] ? iconMap[category.iconName] : Book;
                    const catPages = pages.filter(p => p.categoryId === category.id);

                    return (
                      <Caixa id="wiki.categorias.item" repetida key={category.id}>
                      <Link key={category.id} to={`/wiki/${category.slug}`}>
                        <Card className="bg-card border-border hover:border-primary/50 transition-all h-full">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Icon className="w-6 h-6 text-primary" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-lg text-foreground mb-1">
                                  {category.name}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {catPages.length} artigos
                                </p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                      </Caixa>
                    );
                  })}
                </div>
                  </Caixa>
                )}

                {/* Most Viewed Articles */}
                {mostViewedPages.length > 0 && (
                  <Caixa id="wiki.populares">
                  <div className="mt-12">
                    <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                      🔥 Artigos Mais Visualizados
                    </h2>
                    <div className="space-y-3">
                      {mostViewedPages.map((page, index) => (
                        <Caixa id="wiki.populares.item" repetida key={page.id}>
                        <Link
                          key={page.id}
                          to={`/wiki/${page.categorySlug}/${page.slug}`}
                        >
                          <Card className="bg-card border-border hover:border-primary/50 transition-all group">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <span className="text-lg font-bold text-primary">
                                    {index + 1}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1 mb-1">
                                    {page.title}
                                  </h3>
                                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Book className="w-3.5 h-3.5" />
                                      {page.categoryName}
                                    </span>
                                    <span>•</span>
                                  <span>{(page.views || 0).toLocaleString()} visualizações</span>
                                  </div>
                                  {page.tags && page.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {page.tags.slice(0, 3).map((tag) => {
                                        const tagColor = tag.color || 'hsl(var(--primary))';
                                        return (
                                          <span
                                            key={tag.id}
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                                            style={{
                                              backgroundColor: `${tagColor}15`,
                                              color: tagColor,
                                              borderColor: `${tagColor}30`,
                                              borderWidth: '1px',
                                            }}
                                          >
                                            <Tag className="w-2.5 h-2.5" />
                                            {tag.name}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                        </Caixa>
                      ))}
                    </div>
                  </div>
                  </Caixa>
                )}
              </>
            ) : currentPage ? (
              /* Article View */
              <div>
                <Caixa id="wiki.artigo.voltar">
                <Link
                  to={`/wiki/${currentCategory?.slug}`}
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar para {currentCategory?.name}
                </Link>
                </Caixa>

                <Caixa id="wiki.artigo" miolo>
                <Card className="bg-card border-border">
                  <CardContent className="p-8">
                    <h1 className="text-3xl font-bold text-foreground mb-4">
                      {currentPage.title}
                    </h1>

                    {/* Tags */}
                    {currentPage.tags && currentPage.tags.length > 0 && (
                      <Caixa id="wiki.artigo.tags">
                      <div className="flex flex-wrap gap-2 mb-6">
                        {currentPage.tags.map((tag) => {
                          const tagColor = tag.color || 'hsl(var(--primary))';
                          const isSelected = selectedTags.includes(tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => {
                                toggleTag(tag.id);
                                // Navigate back to category view to see filtered results
                                window.location.href = `/wiki${currentCategory ? `/${currentCategory.slug}` : ''}`;
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-all hover:opacity-80 ${
                                isSelected ? 'ring-2 ring-offset-2 ring-offset-background' : ''
                              }`}
                              style={{
                                backgroundColor: isSelected ? tagColor : `${tagColor}20`,
                                color: isSelected ? '#fff' : tagColor,
                                borderWidth: '1px',
                                borderColor: tagColor,
                              }}
                            >
                              <Tag className="w-3.5 h-3.5" />
                              {tag.name}
                            </button>
                          );
                        })}
                      </div>
                      </Caixa>
                    )}

                    <div
                      className="prose prose-invert max-w-none
                        prose-headings:text-foreground prose-headings:font-bold
                        prose-p:text-muted-foreground prose-p:leading-relaxed
                        prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-foreground prose-strong:font-semibold
                        prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                        prose-li:marker:text-primary
                        prose-code:text-primary/80 prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                        prose-pre:bg-background prose-pre:border prose-pre:border-border"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentPage.content ?? '') }}
                    />

                    {/* Attachments */}
                    {currentPage.attachments && currentPage.attachments.length > 0 && (
                      <>
                        <Separator className="my-8 bg-border" />
                        <Caixa id="wiki.artigo.anexos">
                        <div>
                          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            <Paperclip className="w-5 h-5" />
                            Anexos
                          </h3>
                          <div className="space-y-2">
                            {currentPage.attachments.map((attachment) => {
                              const fileUrl = attachment.filePath.startsWith('http') 
                                ? attachment.filePath 
                                : `${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}${attachment.filePath}`;
                              
                              return (
                                <div
                                  key={attachment.id}
                                  className="flex items-center gap-3 p-3 rounded-lg bg-muted"
                                >
                                  <Paperclip className="w-5 h-5 text-primary" />
                                  <div className="flex-1">
                                    <p className="text-foreground font-medium">{attachment.fileName}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {(attachment.fileSize / 1024).toFixed(2)} KB
                                    </p>
                                  </div>
                                  <a
                                    href={fileUrl}
                                    download={attachment.fileName}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-border text-foreground hover:bg-muted/80"
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Baixar
                                    </Button>
                                  </a>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        </Caixa>
                      </>
                    )}

                    <EspacoDeAnuncio posicao="ARTICLE_END" className="mt-8" />

                    {/* Feedback */}
                    <Separator className="my-8 bg-border" />
                    <Caixa id="wiki.artigo.avaliacao">
                    <div className="text-center">
                      <p className="text-muted-foreground mb-4">
                        Este artigo foi útil?
                      </p>
                      <div className="flex items-center justify-center gap-4">
                        <Button
                          variant={feedback === 'positive' ? 'default' : 'outline'}
                          className={
                            feedback === 'positive'
                              ? 'bg-success text-success-foreground hover:bg-success/90'
                              : 'border-border text-foreground hover:bg-muted'
                          }
                          onClick={() => handleFeedback(true)}
                          disabled={feedback !== null || submittingFeedback}
                        >
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          Sim
                        </Button>
                        <Button
                          variant={feedback === 'negative' ? 'default' : 'outline'}
                          className={
                            feedback === 'negative'
                              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                              : 'border-border text-foreground hover:bg-muted'
                          }
                          onClick={() => handleFeedback(false)}
                          disabled={feedback !== null || submittingFeedback}
                        >
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          Não
                        </Button>
                      </div>
                    </div>
                    </Caixa>

                    {/* Related Articles */}
                    {getRelatedPages().length > 0 && (
                      <>
                        <Separator className="my-8 bg-border" />
                        <Caixa id="wiki.artigo.relacionados">
                        <div>
                          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                            📚 Artigos Relacionados
                          </h3>
                          <div className="grid grid-cols-1 gap-3">
                            {getRelatedPages().map((relatedPage) => {
                              const relatedCategory = categories.find(c => c.id === relatedPage.categoryId);
                              const commonTags = relatedPage.tags?.filter(t => 
                                currentPage.tags?.some(ct => ct.id === t.id)
                              ) || [];
                              
                              return (
                                <Link
                                  key={relatedPage.id}
                                  to={`/wiki/${relatedCategory?.slug}/${relatedPage.slug}`}
                                  className="block p-4 rounded-lg bg-muted hover:bg-muted/80 transition-all group"
                                >
                                  <div className="flex items-start gap-3">
                                    <Book className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                                        {relatedPage.title}
                                      </h4>
                                      {commonTags.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                          {commonTags.map((tag) => {
                                            const tagColor = tag.color || 'hsl(var(--primary))';
                                            return (
                                              <span
                                                key={tag.id}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                                                style={{
                                                  backgroundColor: `${tagColor}15`,
                                                  color: tagColor,
                                                  borderColor: `${tagColor}30`,
                                                  borderWidth: '1px',
                                                }}
                                              >
                                                <Tag className="w-2.5 h-2.5" />
                                                {tag.name}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                        </Caixa>
                      </>
                    )}

                    {/* Navigation */}
                    {(prev || next) && (
                      <>
                        <Separator className="my-8 bg-border" />
                        <Caixa id="wiki.artigo.navegacao">
                        <div className="flex items-center justify-between">
                          {prev ? (
                            <Link
                              to={`/wiki/${currentCategory?.slug}/${prev.slug}`}
                              className="flex items-center gap-2 text-primary hover:text-primary/80"
                            >
                              <ArrowLeft className="w-4 h-4" />
                              <div>
                                <p className="text-xs text-muted-foreground">Anterior</p>
                                <p className="font-medium">{prev.title}</p>
                              </div>
                            </Link>
                          ) : (
                            <div />
                          )}
                          {next && (
                            <Link
                              to={`/wiki/${currentCategory?.slug}/${next.slug}`}
                              className="flex items-center gap-2 text-primary hover:text-primary/80 text-right"
                            >
                              <div>
                                <p className="text-xs text-muted-foreground">Próximo</p>
                                <p className="font-medium">{next.title}</p>
                              </div>
                              <ChevronRight className="w-4 h-4" />
                            </Link>
                          )}
                        </div>
                        </Caixa>
                      </>
                    )}
                  </CardContent>
                </Card>
                </Caixa>
              </div>
            ) : (
              /* Category Article List */
              <div>
                <Caixa id="wiki.categoria.cabecalho">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {currentCategory?.name}
                  </h2>
                  <p className="text-muted-foreground">
                    {categoryPages.length} {categoryPages.length === 1 ? 'artigo' : 'artigos'}
                  </p>
                </div>
                </Caixa>

                {categoryPages.length === 0 ? (
                  <Caixa id="wiki.categoria.vazio" miolo>
                  <Card className="border-card-border">
                    <CardContent className="p-12">
                      <div className="flex flex-col items-center gap-4 text-center">
                        {/*
                          Mesma bolha dos outros vazios da loja: chapada, na
                          cor do lojista. O gradiente anterior puxava metade
                          para a cor de destaque, então o ícone aparecia num
                          tom que a loja não escolheu para ele.
                        */}
                        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Book className="w-10 h-10 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-foreground mb-2">
                            {t('store.wiki.emptyCategoryTitle')}
                          </h3>
                          <p className="text-muted-foreground text-lg">
                            {t('store.wiki.emptyCategoryBody')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </Caixa>
                ) : (
                  <Caixa id="wiki.categoria.artigos">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categoryPages.map((page) => (
                      <Caixa id="wiki.categoria.artigo" repetida key={page.id}>
                      <div key={page.id}>
                        <Card className="bg-card border-border hover:border-primary/50 transition-all h-full group">
                          <CardContent className="p-5">
                            <Link
                              to={`/wiki/${currentCategory?.slug}/${page.slug}`}
                              className="block"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <Book className="w-5 h-5 text-primary mt-0.5" />
                                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>
                              <h3 className="font-bold text-lg text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                                {page.title}
                              </h3>
                            </Link>
                            
                            {/* Tags */}
                            {page.tags && page.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {page.tags.map((tag) => {
                                  const tagColor = tag.color || 'hsl(var(--primary))';
                                  const isSelected = selectedTags.includes(tag.id);
                                  return (
                                    <button
                                      key={tag.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTag(tag.id);
                                      }}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all hover:opacity-80 ${
                                        isSelected ? 'ring-1 ring-offset-1 ring-offset-background' : ''
                                      }`}
                                      style={{
                                        backgroundColor: isSelected ? tagColor : `${tagColor}15`,
                                        color: isSelected ? '#fff' : tagColor,
                                        borderColor: `${tagColor}30`,
                                        borderWidth: '1px',
                                      }}
                                    >
                                      <Tag className="w-3 h-3" />
                                      {tag.name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Eye className="w-4 h-4" />
                                {(page.views || 0).toLocaleString()}
                              </span>
                              {page.isFeatured && (
                                <>
                                  <span>•</span>
                                  <span className="text-primary font-medium">{t('store.wiki.featured')}</span>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                      </Caixa>
                    ))}
                  </div>
                  </Caixa>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
